import React, { useState, useEffect } from "react";
import {
  X,
  Share2,
  Check,
  Tag,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  ShoppingBag,
  ExternalLink,
} from "lucide-react";
import { Product } from "../../data/catalog";
import { WHATSAPP_NUMBER } from "../../config/constants";
import { DeliveryOption } from "../../data/deliveryConfig";
import { useDeliveryOptions } from "../../hooks/useDeliveryOptions";
import DeliveryZoneSelector from "../ui/DeliveryZoneSelector";
import { isVideoUrl } from "../../utils/mediaUtils";
import { getOptimizedImageUrl } from "../../utils/imageOptimization";
import ImageLightbox from "../ui/ImageLightbox";
import OptimizedImage from "../ui/OptimizedImage";

interface ProductDetailModalProps {
  product: (Product & { images?: string[] }) | null;
  isOpen: boolean;
  onClose: () => void;
  onNavigateNext?: () => void;
  onNavigatePrev?: () => void;
}

const badgeColors: Record<string, string> = {
  Favorita: "bg-rose-100 text-rose-700 border-rose-200",
  Popular: "bg-pink-100 text-pink-700 border-pink-200",
  Especial: "bg-amber-100 text-amber-800 border-amber-200",
  Nuevo: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

export const ProductDetailModal: React.FC<ProductDetailModalProps> = ({
  product,
  isOpen,
  onClose,
  onNavigateNext,
  onNavigatePrev,
}) => {
  const [selectedSizeIndex, setSelectedSizeIndex] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const deliveryOptions = useDeliveryOptions();
  const [selectedDeliveryId, setSelectedDeliveryId] = useState("pickup");
  const [selectedDeliveryPrice, setSelectedDeliveryPrice] = useState(0);
  const selectedDelivery =
    deliveryOptions.find((o) => o.id === selectedDeliveryId) || deliveryOptions[0];

  const handleDeliveryChange = (opt: DeliveryOption, price: number) => {
    setSelectedDeliveryId(opt.id);
    setSelectedDeliveryPrice(opt.type === "pickup" ? 0 : price);
  };

  // Reset indices when product changes
  useEffect(() => {
    setSelectedSizeIndex(0);
    setCurrentImageIndex(0);
    setCopied(false);
    setSelectedDeliveryId("pickup");
    setSelectedDeliveryPrice(0);
  }, [product?.id]);

  // Handle ESC key and arrows
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowRight" && onNavigateNext) {
        onNavigateNext();
      } else if (e.key === "ArrowLeft" && onNavigatePrev) {
        onNavigatePrev();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose, onNavigateNext, onNavigatePrev]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen || !product || product.available === false) return null;

  // Extract all images
  const allImages = product.images && product.images.length > 0
    ? product.images
    : product.image
      ? product.image.includes(",")
        ? product.image.split(",").map((s) => s.trim()).filter(Boolean)
        : [product.image]
      : [];

  const currentImage = allImages[currentImageIndex] || "";
  const currentIsVideo = isVideoUrl(currentImage);
  const selectedPrice = product.prices[selectedSizeIndex] || product.prices[0] || { people: "Estándar", price: 0 };

  const handleCopyLink = () => {
    const url = `https://estefscakes.com/#postre-${product.id}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2800);
    });
  };

  const deliveryInfo = selectedDelivery.type === "pickup"
    ? `Pick Up en ${selectedDelivery.address}`
    : `Delivery ${selectedDelivery.zone}`;

  const totalPrice = selectedPrice.price + (selectedDelivery.type === "delivery" ? selectedDeliveryPrice : 0);

  const whatsappMessage = encodeURIComponent(
    `¡Hola Estef's Cakes! 🍰\n\nMe gustaría pedir este postre:\n*${product.name}*\n• Tamaño/Porción: ${selectedPrice.people}\n• Precio producto: ${selectedPrice.price} €${selectedDelivery.type === "delivery" ? `\n• Delivery: ${selectedDelivery.zone} ${selectedDeliveryPrice} €` : ""}\n• Total: ${totalPrice} €\n• Zona de entrega: ${deliveryInfo}\n\n¿Tienen disponibilidad?`
  );

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-rose-950/40 backdrop-blur-md transition-opacity animate-fadeIn"
          onClick={onClose}
          aria-hidden="true"
        />

        {/* Modal Window */}
        <div
          className="relative w-full max-w-3xl lg:max-w-4xl bg-white rounded-3xl shadow-2xl border border-rose-100/80 overflow-hidden z-10 animate-scaleUp max-h-[92vh] md:max-h-[88vh] flex flex-col my-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="product-detail-title"
        >
          {/* Top Bar with actions */}
          <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-rose-100/60 bg-gradient-to-r from-rose-50/70 via-white to-pink-50/50 flex-shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-xl">{product.emoji}</span>
              <span className="font-body text-xs font-semibold text-rose-600 uppercase tracking-wider">
                Detalle del Postre
              </span>
            </div>

            <div className="flex items-center gap-2">
              {/* Copy share link */}
              <button
                type="button"
                onClick={handleCopyLink}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all shadow-sm ${
                  copied
                    ? "bg-emerald-600 text-white shadow-emerald-200 scale-105"
                    : "bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200"
                }`}
                title="Copiar enlace para compartir o publicidad"
              >
                {copied ? (
                  <>
                    <Check className="w-3.5 h-3.5" />
                    <span>¡Enlace Copiado!</span>
                  </>
                ) : (
                  <>
                    <Share2 className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Compartir</span>
                  </>
                )}
              </button>

              {/* Close Button */}
              <button
                type="button"
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-rose-100/80 hover:bg-rose-200 text-rose-800 flex items-center justify-center transition-colors"
                aria-label="Cerrar modal"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Modal Body — dos columnas en desktop, scroll vertical fluido en mobile */}
          <div className="flex flex-col md:flex-row flex-1 min-h-0 overflow-y-auto md:overflow-hidden">

            {/* Columna 1: Imagen — altura controlada en mobile para dejar espacio al contenido */}
            <div className="w-full md:w-[45%] flex-shrink-0 flex flex-col bg-rose-50/60">
              <div className="relative h-48 sm:h-64 md:h-auto md:flex-1 overflow-hidden group">
                {currentImage ? (
                  currentIsVideo ? (
                    <video
                      src={currentImage}
                      controls
                      playsInline
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <OptimizedImage
                      path={currentImage}
                      alt={product.name}
                      width={900}
                      className="w-full h-full object-cover cursor-zoom-in transition-transform duration-500 group-hover:scale-105"
                      onClick={() => setLightboxImage(getOptimizedImageUrl(currentImage, 1400, 90))}
                    />
                  )
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-8xl md:text-9xl opacity-30">{product.emoji}</span>
                  </div>
                )}

                {/* Controls para múltiples imágenes */}
                {allImages.length > 1 && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
                      }}
                      className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-rose-800 p-2 rounded-full shadow-lg transition-all"
                      aria-label="Foto anterior"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-rose-800 p-2 rounded-full shadow-lg transition-all"
                      aria-label="Siguiente foto"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    {/* Indicador de imagen actual */}
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                      {allImages.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(idx); }}
                          className={`w-1.5 h-1.5 rounded-full transition-all ${idx === currentImageIndex ? "bg-white scale-125" : "bg-white/50"}`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Thumbnails strip */}
              {allImages.length > 1 && (
                <div className="flex gap-2 p-3 overflow-x-auto scrollbar-hide bg-white/60 border-t border-rose-100/50">
                  {allImages.map((img, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`relative w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 border-2 transition-all ${
                        idx === currentImageIndex
                          ? "border-rose-600 ring-2 ring-rose-200 scale-95"
                          : "border-transparent opacity-60 hover:opacity-100"
                      }`}
                    >
                      {isVideoUrl(img) ? (
                        <div className="w-full h-full bg-rose-900 flex items-center justify-center text-white text-[9px] font-bold">
                          VIDEO
                        </div>
                      ) : (
                        <img
                          src={img}
                          alt={`${product.name} ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Columna 2: Info — con scroll propio */}
            <div className="flex-1 overflow-y-auto flex flex-col min-h-0">
              <div className="p-5 sm:p-6 flex flex-col flex-1 space-y-4">
                <div>
                  {/* Badge */}
                  {product.badge && (
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      <span
                        className={`inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-0.5 rounded-full border ${
                          badgeColors[product.badge] || "bg-rose-100 text-rose-700 border-rose-200"
                        }`}
                      >
                        <Sparkles className="w-2.5 h-2.5" />
                        {product.badge}
                      </span>
                    </div>
                  )}

                  {/* Title */}
                  <h2
                    id="product-detail-title"
                    className="font-display text-xl sm:text-2xl font-bold text-rose-950 leading-tight mb-2"
                  >
                    {product.name}
                  </h2>

                  {/* Description */}
                  <div className="bg-rose-50/50 p-3 rounded-2xl border border-rose-100/60 mb-4">
                    <p className="font-body text-xs sm:text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                      {product.description || "Deliciosa creación artesanal preparada con los mejores ingredientes y el toque exclusivo de Estef's Cakes."}
                    </p>
                  </div>

                  {/* Size & Pricing Selector */}
                  <div className="space-y-2">
                    <label className="flex items-center justify-between text-[11px] font-bold text-rose-950 uppercase tracking-wider">
                      <span className="flex items-center gap-1.5">
                        <Tag className="w-3 h-3 text-rose-600" />
                        Tamaño / Porciones:
                      </span>
                      <span className="text-[10px] text-rose-500 font-medium lowercase">
                        {product.prices.length} {product.prices.length === 1 ? "opción" : "opciones"}
                      </span>
                    </label>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {product.prices.map((priceOption, idx) => {
                        const isSelected = idx === selectedSizeIndex;
                        return (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => setSelectedSizeIndex(idx)}
                            className={`p-2.5 rounded-xl border-2 text-left transition-all flex items-center justify-between gap-2.5 ${
                              isSelected
                                ? "bg-rose-50/80 border-rose-600 ring-2 ring-rose-100 shadow-sm"
                                : "bg-white border-rose-100/80 hover:border-rose-300 hover:bg-rose-50/30"
                            }`}
                          >
                            <div className="min-w-0">
                              <p className={`font-body text-xs truncate ${
                                isSelected ? "text-rose-950 font-bold" : "text-gray-700 font-medium"
                              }`}>
                                {priceOption.people}
                              </p>
                              <span className="text-[9px] text-gray-400">Porción</span>
                            </div>

                            <div className="text-right flex-shrink-0">
                              <span className={`font-display text-sm font-bold ${
                                isSelected ? "text-rose-700" : "text-rose-900"
                              }`}>
                                <span className="text-[10px] font-normal text-rose-500">Ref</span> {priceOption.price}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Delivery Zone Selector */}
                <div className="pt-2">
                  <DeliveryZoneSelector
                    options={deliveryOptions}
                    selectedId={selectedDelivery?.id || "pickup"}
                    selectedPrice={selectedDeliveryPrice}
                    onChange={handleDeliveryChange}
                  />
                </div>

                {/* Bottom WhatsApp Order CTA */}
                <div className="pt-3 mt-auto border-t border-rose-100/60 space-y-2">
                  <div className="flex items-center justify-between bg-emerald-50/50 p-2.5 rounded-xl border border-emerald-100">
                    <div>
                      <p className="text-[10px] font-bold text-emerald-950 uppercase tracking-wide">
                        Total Seleccionado
                      </p>
                      <p className="text-xs text-emerald-700 font-medium">
                        {selectedPrice.people}
                        {selectedDelivery.type === "delivery" && (
                          <>
                            {" "}
                            <span className="text-[9px] text-emerald-600/80 font-normal">
                              + delivery {selectedDelivery.zone} (Ref {selectedDeliveryPrice})
                            </span>
                          </>
                        )}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="font-display text-xl font-black text-emerald-800">
                        <span className="text-xs font-normal text-emerald-600">Ref</span> {totalPrice}
                      </span>
                    </div>
                  </div>

                  <a
                    href={`https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMessage}`}
                    target="_blank"
                    rel="noreferrer"
                    className="w-full py-3 px-5 bg-gradient-to-r from-rose-700 to-rose-800 hover:from-rose-800 hover:to-rose-900 text-white rounded-xl font-body font-bold text-sm shadow-md hover:shadow-lg active:scale-[0.99] transition-all flex items-center justify-center gap-2"
                  >
                    <ShoppingBag className="w-4 h-4" />
                    <span>Pedir por WhatsApp</span>
                    <ExternalLink className="w-3.5 h-3.5 opacity-70" />
                  </a>

                  <p className="text-[10px] text-center text-gray-400 font-medium">
                    Atención personalizada • Pedidos con anticipación
                    {selectedDelivery.type === "pickup"
                      ? ` • Pick-up en ${selectedDelivery.address}`
                      : ` • Delivery a ${selectedDelivery.zone} (+Ref ${selectedDeliveryPrice})`}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ImageLightbox
        image={lightboxImage}
        onClose={() => setLightboxImage(null)}
        allImages={allImages}
        currentImageIndex={currentImageIndex}
      />
    </>
  );
};

export default ProductDetailModal;
